import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect, useRef } from "react";
import { Redirect, Route, Switch } from "wouter";
import CookieBanner from "@/components/CookieBanner";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LocaleProvider } from "./contexts/LocaleContext";
import Home from "./pages/Home";
import { useWhatsAppPopup } from "./components/WhatsAppPopup";
import AnimatedAvatar from "./components/AnimatedAvatar";
import ImmozwergPageLoader from "./components/ImmozwergPageLoader";
import Breadcrumb from "./components/Breadcrumb";
import BlogSearch from "./components/BlogSearch";
import SiteFooter from "./components/SiteFooter";
import StaticContentTranslationLayer from "./components/StaticContentTranslationLayer";
import { useLocation } from "wouter";

// Die Startseite bleibt für schnellen LCP direkt verfügbar. Alle anderen Seiten
// werden erst bei Navigation geladen; AppSSR rendert dieselben Seiten synchron.
const NotFound = lazy(() => import("@/pages/NotFound"));
const Kampagne = lazy(() => import("./pages/Kampagne"));
const UeberMich = lazy(() => import("./pages/UeberMich"));
const Finanzierung = lazy(() => import("./pages/Finanzierung"));
const Erbschaft = lazy(() => import("./pages/Erbschaft"));
const Immobilien = lazy(() => import("./pages/Immobilien"));
const Leistungen = lazy(() => import("./pages/Leistungen"));
const Team = lazy(() => import("./pages/Team"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ImmobilienDetail = lazy(() => import("./pages/ImmobilienDetail"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Merkliste = lazy(() => import("./pages/Merkliste"));
const Immobilienbewertung = lazy(() => import("./pages/Immobilienbewertung"));
const DeinNeuerWohnort = lazy(() => import("./pages/DeinNeuerWohnort"));
const Dokumentenhilfe = lazy(() => import("./pages/Dokumentenhilfe"));
const ImmobilienmaklerOrt = lazy(() => import("./pages/ImmobilienmaklerOrt"));

/**
 * Interne Seitenwechsel starten oben. Hash-Ziele und die vom Browser verwaltete
 * Zurück-Navigation behalten bewusst ihre eigene Scrollposition.
 */
function ScrollToTopOnNavigation() {
  const [location] = useLocation();
  const isInitialRender = useRef(true);
  const isHistoryNavigation = useRef(false);

  useEffect(() => {
    const markHistoryNavigation = () => {
      isHistoryNavigation.current = true;
    };
    window.addEventListener("popstate", markHistoryNavigation);
    return () => window.removeEventListener("popstate", markHistoryNavigation);
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (isHistoryNavigation.current || location.includes("#")) {
      isHistoryNavigation.current = false;
      return;
    }
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
  }, [location]);

  return null;
}

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Switch>
      <Route path={"/"} component={Home} />
      <Route path={"/kampagne"} component={Kampagne} />
      <Route path={"/ueber-mich"} component={UeberMich} />
      <Route path={"/finanzierung"} component={Finanzierung} />
      <Route path={"/erbschaft"} component={Erbschaft} />
      <Route path={"/immobilien"} component={Immobilien} />
      <Route path={"/immobilienbewertung"} component={Immobilienbewertung} />
      <Route path={"/umziehen-in-heilbronn"} component={DeinNeuerWohnort} />
      <Route path={"/dein-neuer-wohnort"}><Redirect to="/umziehen-in-heilbronn" /></Route>
      <Route path={"/einsatzgebiet"}><Redirect to="/#einsatzgebiet" /></Route>
      <Route path={"/immobilienmakler/:ort"} component={ImmobilienmaklerOrt} />
      <Route path={"/dokumentenhilfe"} component={Dokumentenhilfe} />
      <Route path={"/merkliste"} component={Merkliste} />
      <Route path={"/immobilien/:slug"} component={ImmobilienDetail} />
      <Route path={"/leistungen"} component={Leistungen} />
      <Route path={"/team"} component={Team} />
      <Route path={"/faq"} component={FAQ} />
      <Route path={"/kontakt"} component={Kontakt} />
      <Route path={"/impressum"} component={Impressum} />
      <Route path={"/datenschutz"} component={Datenschutz} />
      <Route path={"/blog"} component={Blog} />
      <Route path={"/blog/:slug"} component={BlogPost} />
      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
  );
}

// Globale Overlays: WhatsApp-Popup + Animierter Avatar (nur Startseite)
// Der Avatar übernimmt die WhatsApp-Funktion – kein separater Floating-Button
function GlobalOverlays() {
  const { openWhatsApp, popupElement } = useWhatsAppPopup();
  const [location] = useLocation();
  const isHome = location === "/";

  return (
    <>
      {popupElement}
      {isHome && <AnimatedAvatar onWhatsAppClick={openWhatsApp} />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <ErrorBoundary>
      <LocaleProvider>
      <ThemeProvider
        defaultTheme="light"
      >
          <TooltipProvider>
          <StaticContentTranslationLayer />
          <ScrollToTopOnNavigation />
          <Breadcrumb />
          <BlogSearch />
          <Suspense fallback={<ImmozwergPageLoader />}>
            <Router />
          </Suspense>
            <SiteFooter />
            <GlobalOverlays />
            <CookieBanner />
          </TooltipProvider>
      </ThemeProvider>
      </LocaleProvider>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
